import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@heroui/react';
import { MOD_KEY } from '@/lib/platform';
import { Tooltip } from '@/components/Tooltip';
import {
  wrapBold,
  wrapCode,
  wrapItalic,
  wrapLink,
  wrapQuote,
  wrapSpoiler,
  wrapStrike,
  wrapUnderline,
  type WrapResult,
} from '@/lib/formatWrap';
import {
  BoldIcon,
  CodeIcon,
  EyeIcon,
  ItalicIcon,
  LinkIcon,
  QuoteIcon,
  SpoilerIcon,
  StrikethroughIcon,
  UnderlineIcon,
} from '@/components/icons';

/**
 * Slim formatting toolbar above the composer textarea. Buttons mirror
 * the keyboard shortcuts wired in the textarea's onKeyDown — both call
 * applyFormat() so the wrap behaviour stays in one place.
 *
 * Visual register: outline glyphs, 26×26 hit area, no labels (titles
 * carry the keyboard hint). Lives in a low-contrast wrap so a calm
 * eye scanning the composer doesn't read it before the input.
 */
export function FormatToolbar(props: {
  onApply: (fn: (s: string, a: number, b: number) => WrapResult) => void;
  previewOn: boolean;
  onTogglePreview: () => void;
}) {
  const { t } = useTranslation();
  // Modifier key label adapts to platform so Mac users see ⌘B instead
  // of Ctrl B in tooltip hints.
  const mod = MOD_KEY;
  const buttons: Array<{
    title: string;
    onClick: () => void;
    icon: React.ReactNode;
  }> = [
    {
      title: t('ui2.format.bold', { mod }),
      onClick: () => props.onApply(wrapBold),
      icon: <BoldIcon className="h-3.5 w-3.5" />,
    },
    {
      title: t('ui2.format.italic', { mod }),
      onClick: () => props.onApply(wrapItalic),
      icon: <ItalicIcon className="h-3.5 w-3.5" />,
    },
    {
      title: t('ui2.format.underline', { mod }),
      onClick: () => props.onApply(wrapUnderline),
      icon: <UnderlineIcon className="h-3.5 w-3.5" />,
    },
    {
      title: t('ui2.format.strike', { mod }),
      onClick: () => props.onApply(wrapStrike),
      icon: <StrikethroughIcon className="h-3.5 w-3.5" />,
    },
    {
      title: t('ui2.format.code', { mod }),
      onClick: () => props.onApply(wrapCode),
      icon: <CodeIcon className="h-3.5 w-3.5" />,
    },
    {
      title: t('ui2.format.spoiler', { mod }),
      onClick: () => props.onApply(wrapSpoiler),
      icon: <SpoilerIcon className="h-3.5 w-3.5" />,
    },
    {
      title: t('ui2.format.quote', { mod }),
      onClick: () => props.onApply(wrapQuote),
      icon: <QuoteIcon className="h-3.5 w-3.5" />,
    },
    {
      title: t('ui2.format.link', { mod }),
      onClick: () => props.onApply(wrapLink),
      icon: <LinkIcon className="h-3.5 w-3.5" />,
    },
  ];
  return (
    <div className="mb-1.5 flex flex-wrap items-center gap-0.5 px-1 text-ink-muted">
      {/* `preventFocusOnPress` keeps the textarea's selection intact (the
          format helpers read it). */}
      {buttons.map((b) => (
        <Tooltip key={b.title} label={b.title}>
          <Button
            isIconOnly
            size="sm"
            variant="ghost"
            preventFocusOnPress
            onPress={b.onClick}
            aria-label={b.title}
          >
            {b.icon}
          </Button>
        </Tooltip>
      ))}
      {/* Preview toggle is grouped on the right with a hairline separator
          so the user reads it as a different category — "what you'll see"
          vs "what you'll do to the text". Active state uses --bk-accent so
          it reads as "on" without ambiguity. */}
      <span aria-hidden className="mx-1 h-4 w-px bg-ink/10" />
      <Tooltip label={props.previewOn ? t('ui2.format.hidePreview') : t('ui2.format.showPreview')}>
        <button
          type="button"
          aria-label={props.previewOn ? t('ui2.format.hidePreview') : t('ui2.format.showPreview')}
          aria-pressed={props.previewOn}
          onMouseDown={(e) => {
            e.preventDefault();
            props.onTogglePreview();
          }}
          className={
            'grid h-9 w-9 place-items-center rounded-lg transition-colors hover:bg-white/10 hover:text-ink md:h-7 md:w-7 ' +
            (props.previewOn ? 'bg-accent/15 text-accent' : '')
          }
        >
          {props.previewOn ? (
            <EyeIcon className="h-3.5 w-3.5" />
          ) : (
            <SpoilerIcon className="h-3.5 w-3.5" />
          )}
        </button>
      </Tooltip>
    </div>
  );
}
